import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

import { View as OnboardingView } from "@/admin/onboarding/View";
import Loading from "@/components/Loading";
import { getBookingTimeSettingsFn } from "@/server-fns/admin/settings/bookingTimeFns";
import { getHouseRulesFn } from "@/server-fns/admin/settings/houseRulesFns";
import { getDiningTablesFn } from "@/server-fns/admin/settings/tablesFns";
import { getCurrentStoreStatusFn } from "@/server-fns/admin/storeFns";

export const Route = createFileRoute("/onboarding")({
    ssr: false,
    component: RouteComponent,
});

function RouteComponent() {
    const navigate = useNavigate();
    const { data: storeStatus, isLoading } = useQuery({
        queryKey: ["currentStoreStatus"],
        queryFn: () => getCurrentStoreStatusFn(),
    });

    const inProgressStoreId =
        storeStatus?.status === "in-progress" ? storeStatus.store.id : undefined;

    const { data: bookingTime, isLoading: isBookingTimeLoading } = useQuery({
        queryKey: ["onboardingResume", "bookingTime", inProgressStoreId],
        queryFn: () => getBookingTimeSettingsFn({ data: { storeId: inProgressStoreId! } }),
        enabled: !!inProgressStoreId,
    });
    const { data: tables, isLoading: isTablesLoading } = useQuery({
        queryKey: ["onboardingResume", "tables", inProgressStoreId],
        queryFn: () => getDiningTablesFn({ data: { storeId: inProgressStoreId! } }),
        enabled: !!inProgressStoreId,
    });
    const { data: rules, isLoading: isRulesLoading } = useQuery({
        queryKey: ["onboardingResume", "rules", inProgressStoreId],
        queryFn: () => getHouseRulesFn({ data: { storeId: inProgressStoreId! } }),
        enabled: !!inProgressStoreId,
    });

    const isResumeDataLoading =
        !!inProgressStoreId && (isBookingTimeLoading || isTablesLoading || isRulesLoading);

    useEffect(() => {
        if (isLoading || !storeStatus) return;
        if (storeStatus.status === "unauthenticated") {
            navigate({ to: "/signin" });
        } else if (storeStatus.status === "ok") {
            navigate({
                to: "/app/$storeId",
                params: { storeId: storeStatus.store.id },
                search: { date: undefined, bookingId: undefined },
            });
        }
    }, [isLoading, storeStatus, navigate]);

    if (isLoading || !storeStatus) {
        return <Loading />;
    }

    if (storeStatus.status !== "no-store" && storeStatus.status !== "in-progress") {
        return <Loading />;
    }

    if (storeStatus.status === "in-progress") {
        if (isResumeDataLoading || !bookingTime || !tables || !rules) {
            return <Loading />;
        }
        return (
            <OnboardingView
                existingStore={storeStatus.store}
                initialBookingTime={bookingTime}
                initialTables={tables}
                initialRules={rules}
            />
        );
    }

    return <OnboardingView />;
}
